// features/findings/constraint-maneuver.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — The Constraint Maneuver (v1)
// Form 4 (Pattern across cases). Methodology of breakthrough science.
//
// Pattern: scientific breakthroughs that move a phenomenon from
// "observed but unexplained" to "mechanism revealed" deploy a specific
// move — design a constraint into the experimental setup such that
// the system can't avoid exposing the variable you want to study.
//
// Seven cases across 400 years; one failed analog (Kelvin) shows
// the mechanism's failure mode.
//
// Pre-registered 2026-05-09. Strategic-domain because methodology
// IS decision-relevant: identifying "what's the constraint move?"
// turns intractable research/bet questions into tractable ones.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
  FindingCite,
} from './components-shared';
import {
  CoOccurrenceMatrix,
  TornadoPlot,
  PredictionsBlock,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Cases × constraint-type matrix. Shows that the same case often
// uses MORE THAN ONE constraint sub-type — they compound.
const MATRIX_CASES = [
  { id: 'galileo',   shortLabel: 'Galileo 1602',      outcome: 'success' },
  { id: 'cavendish', shortLabel: 'Cavendish 1798',    outcome: 'success' },
  { id: 'pasteur',   shortLabel: 'Pasteur 1859',      outcome: 'success' },
  { id: 'mendel',    shortLabel: 'Mendel 1866',       outcome: 'success' },
  { id: 'michelson', shortLabel: 'Michelson 1887',    outcome: 'success' },
  { id: 'tyson',     shortLabel: 'Tyson plant',        outcome: 'success' },
  { id: 'tyson-o2',  shortLabel: 'Tyson O₂ Fermi',     outcome: 'success' },
  { id: 'kelvin',    shortLabel: 'Kelvin 1862',        outcome: 'failure' },
];

const MATRIX_SIGNALS = [
  {
    id: 'disentangle',
    label: 'Disentangle confounded variables',
    values: {
      galileo:    { state: 'absent' },
      cavendish:  { state: 'absent' },
      pasteur:    { state: 'present' },
      mendel:     { state: 'present' },
      michelson:  { state: 'present' },
      tyson:      { state: 'present' },
      'tyson-o2': { state: 'absent' },
      kelvin:     { state: 'absent' },
    },
  },
  {
    id: 'magnify',
    label: 'Magnify a small effect',
    values: {
      galileo:    { state: 'present' },
      cavendish:  { state: 'present' },
      pasteur:    { state: 'absent' },
      mendel:     { state: 'absent' },
      michelson:  { state: 'absent' },
      tyson:      { state: 'absent' },
      'tyson-o2': { state: 'absent' },
      kelvin:     { state: 'present' },
    },
  },
  {
    id: 'invert',
    label: 'Invert a normal condition',
    values: {
      galileo:    { state: 'absent' },
      cavendish:  { state: 'absent' },
      pasteur:    { state: 'absent' },
      mendel:     { state: 'absent' },
      michelson:  { state: 'present' },
      tyson:      { state: 'present' },
      'tyson-o2': { state: 'absent' },
      kelvin:     { state: 'absent' },
    },
  },
  {
    id: 'fermi',
    label: 'Quantify a vague intuition',
    values: {
      galileo:    { state: 'absent' },
      cavendish:  { state: 'absent' },
      pasteur:    { state: 'absent' },
      mendel:     { state: 'absent' },
      michelson:  { state: 'absent' },
      tyson:      { state: 'absent' },
      'tyson-o2': { state: 'present' },
      kelvin:     { state: 'partial', detail: 'hidden variable invalidated' },
    },
  },
  {
    id: 'measure',
    label: 'Measure what intuition gets wrong',
    values: {
      galileo:    { state: 'partial', detail: 'measurement enabled by setup' },
      cavendish:  { state: 'partial', detail: 'measurement enabled by setup' },
      pasteur:    { state: 'absent' },
      mendel:     { state: 'absent' },
      michelson:  { state: 'absent' },
      tyson:      { state: 'absent' },
      'tyson-o2': { state: 'absent' },
      kelvin:     { state: 'absent' },
    },
  },
];

const FALSIFIER_DIMS = [
  {
    id: 'cherry-pick',
    label: 'Cherry-picked cases vs random sample',
    low: -0.20,
    high: 0.20,
    lowLabel: 'cases were curated for fit — pattern weakens',
    highLabel: 'random sample of Nobel-class experiments fit — pattern holds',
  },
  {
    id: 'generic',
    label: 'Is "constraint" too generic to be a finding?',
    low: -0.25,
    high: 0.15,
    lowLabel: '"experimental design 101" — pattern undershoots',
    highLabel: 'sub-types distinguish from generic experimental design',
  },
  {
    id: 'transfer',
    label: 'Cross-domain transfer (science → strategy)',
    low: -0.20,
    high: 0.20,
    lowLabel: 'works in physics; doesn\'t transfer to operators / investors',
    highLabel: 'documented constraint-moves in 3+ strategic-decision cases',
  },
  {
    id: 'kelvin-rate',
    label: 'Frequency of Kelvin-class failures',
    low: -0.15,
    high: 0.20,
    lowLabel: 'most constraint-maneuvers succeed; failure mode rare',
    highLabel: 'hidden-variable failures are common; pattern less actionable',
  },
];

export const CONSTRAINT_PREDICTIONS = [
  {
    label: 'Application to future Lab findings: applying the constraint-maneuver lens to the next 5 Form-1/3/4/5 findings authored will identify the operative constraint move in ≥4 of them.',
    central: 4,
    band: [3, 5],
    falsifierBand: [0, 2],
    resolutionDate: '2026-11 (after 5 more findings authored)',
    status: 'open',
    note: 'Tests whether the pattern is genuinely useful or post-hoc storytelling. Falsified if the lens can\'t identify the move in most cases.',
  },
  {
    label: 'Stuck-research identification: identify 3 currently-stuck research questions in the Lab corpus where introducing a constraint would unstick them. Within 18 months, attempt to author findings on ≥2.',
    resolutionDate: '2027-11',
    status: 'open',
    note: 'Tests prospective usefulness. The constraint lens should generate research moves, not just retrospectively label them.',
  },
  {
    label: 'Cross-domain transfer: the constraint maneuver appears in 3+ Strategic Playbook dossier cases where the operator\'s success used an identifiable constraint move (not generic "experimental design").',
    central: 3,
    band: [2, 5],
    falsifierBand: [0, 1],
    resolutionDate: '2026-09',
    status: 'open',
    note: 'Tests cross-domain generalization. Required to claim the finding has strategic-decision relevance, not just history-of-science relevance.',
  },
  {
    label: 'Anti-prediction: if the pattern is too generic, a random sample of 10 Nobel-Prize physics / medicine experiments from the last 50 years will fit the constraint-maneuver shape in fewer than 3 cases.',
    central: 6,
    band: [4, 8],
    falsifierBand: [0, 3],
    resolutionDate: '2026-08',
    status: 'open',
    note: 'Tests against the "experimental design 101" objection. If most modern Nobel work doesn\'t cleanly fit the 5 sub-types, the pattern needs sharpening.',
  },
];

export const FINDING_META = {
  slug: 'constraint-maneuver',
  domain: 'strategic',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/strategic/2026/constraint-maneuver/v1',
  title: 'The Constraint Maneuver: How Science Makes the Invisible Legible',
  shortTitle: 'The Constraint Maneuver',
  pageTitle: 'The Constraint Maneuver — Lab Finding · Shift',
  description: 'A methodology pattern across 7 historical and modern cases. Scientific breakthroughs that reveal a hidden mechanism deploy a specific move: design a constraint into the setup such that the system can\'t avoid exposing the variable. Five sub-types; one failed analog (Kelvin) shows the failure mode.',
  form: 4,
  formName: 'Pattern across cases',
  reflexivity: 'LOW',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 7,
  preregisteredAt: '2026-05-09',
  visibility: 'public-peek',
};

export default function ConstraintManeuverFinding() {
  return (
    <article className="finding-article">
      <FindingBack />

      <FindingHeader
        eyebrow="§ Lab finding · strategic/2026/constraint-maneuver/v1"
        title={FINDING_META.title}
        sub="A methodology pattern across 400 years of scientific breakthroughs."
        subItalic="Seven cases share the same epistemic move: design a constraint into the setup such that the system has to expose the variable you want to study. Five sub-types; one failed analog reveals the failure mode."
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          The interesting thing about a scientific breakthrough is rarely the result. The interesting thing is the move that made the result possible. Free fall is too fast for a water-clock; tilt the plane and you can measure it. Gravitational force is too small to see; hang the masses on a torsion wire and you can read the angle. Air-borne particles and air itself look the same when you peer into a flask; bend the neck into an S-curve and you separate them. The pattern across these cases — and across the four others below — is that <strong>the breakthrough is the constraint, not the observation</strong>. When a phenomenon refuses to reveal its mechanism, the move is to design a setup where the system can&rsquo;t avoid committing.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>Pre-registered:</strong> Across 7 historical and modern cases (Galileo, Cavendish, Pasteur, Mendel, Michelson-Morley, Tyson&rsquo;s upside-down plant, Tyson&rsquo;s per-human O₂ Fermi), scientific breakthroughs deploy a constraint maneuver — physically or logically forcing the system to expose a hidden variable. Five sub-types emerge: <em>disentangle</em>, <em>magnify</em>, <em>invert</em>, <em>quantify</em>, <em>measure</em>. The failed analog (Kelvin&rsquo;s Earth-age estimate) shows the mechanism&rsquo;s failure mode: a constraint is only as good as the variable list it controls.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=7 (+1 failed analog)</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-09</FindingPill>
          <FindingPill tone="neutral">Reflexivity: LOW</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Galileo&rsquo;s inclined plane (1602) · Cavendish&rsquo;s torsion balance (1798) · Pasteur&rsquo;s swan-neck flask (1859) · Mendel&rsquo;s pea plant crosses (1866) · Michelson-Morley interferometer (1887) · Neil deGrasse Tyson&rsquo;s upside-down plant (high school) · Tyson&rsquo;s per-human O₂ Fermi estimate · Kelvin&rsquo;s Earth-age estimate (failed analog, 1862-1897).
        </div>
      </section>

      {/* ── §1 The pattern ── */}
      <FindingSection
        eyebrow="§ The pattern"
        title="State it up front."
      >
        <p className="finding-prose">
          When a phenomenon resists direct observation — because it&rsquo;s too small, too fast, too confounded, too vague, or too obscured by intuition — the breakthrough move is to design a constraint into the setup such that the system has no choice but to reveal the variable. <strong>The constraint is the experiment.</strong> The data are a consequence.
        </p>
        <p className="finding-prose">
          Five sub-types of constraint show up across the corpus. They sometimes compound (a single case can deploy two or three at once), but each is independently identifiable:
        </p>
        <ul className="finding-bullets">
          <li><strong>Disentangle</strong> — separate two normally-confounded variables (Pasteur, Mendel, parts of Michelson-Morley and Tyson&rsquo;s plant).</li>
          <li><strong>Magnify</strong> — amplify a small effect with an instrument or geometry that converts a tiny signal into a measurable one (Cavendish, Galileo).</li>
          <li><strong>Invert</strong> — flip a normal condition so the contrast exposes a hidden mechanism (Michelson-Morley, Tyson&rsquo;s plant).</li>
          <li><strong>Quantify</strong> — convert a vague intuition (&ldquo;plants give us oxygen&rdquo;) into a Fermi-decomposed number that bounds the design space (Tyson&rsquo;s per-human O₂; many habitat-engineering questions).</li>
          <li><strong>Measure</strong> — directly observe what intuition systematically gets wrong, displacing inference with empirics (recent green-light absorption studies; chirality and flavor).</li>
        </ul>
      </FindingSection>

      {/* ── §2 The cases ── */}
      <FindingSection
        eyebrow="§ The cases"
        title="Seven examples; the same shape under each."
      >
        <FindingSubhead>1. Galileo&rsquo;s inclined plane (1602) — magnification by dilution</FindingSubhead>
        <p className="finding-prose">
          To test whether falling bodies accelerate uniformly and whether mass affects rate, Galileo needed to time falling objects. Free fall is too fast for the water-clock he had — about half a second to fall a meter, too quick to measure reliably. <strong>The constraint move:</strong> roll a ball down an inclined plane, which reduces the effective acceleration by sin θ. Now a 4-meter run takes several seconds, and timing becomes possible without changing the underlying physics. The constraint preserves the variable of interest (uniform acceleration) while slowing the system to a measurable speed. Result: refutation of Aristotelian &ldquo;heavier falls faster&rdquo;; the foundation of kinematics.
        </p>

        <FindingSubhead>2. Cavendish torsion balance (1798) — instrument-magnification of a tiny force</FindingSubhead>
        <p className="finding-prose">
          Newton&rsquo;s gravitational constant G is on the order of 10⁻¹¹ — far below what could be measured directly between human-scale objects. Cavendish suspended two small lead spheres (1.6 lb each) on a torsion wire near two large lead spheres (350 lb). The gravitational attraction between them rotates the wire by a small but measurable angle. <strong>The constraint move:</strong> a torsion wire converts force to angle with massive mechanical amplification. The angle was measurable; G fell out of the geometry. Result: first measurement of G; first &ldquo;weighing of the Earth.&rdquo;
        </p>

        <FindingSubhead>3. Pasteur&rsquo;s swan-neck flask (1859) — geometric disentanglement</FindingSubhead>
        <p className="finding-prose">
          The spontaneous-generation debate centered on a confound: when nutrient broth grew microbes after exposure to air, were the microbes generated <em>from</em> the air, or were they air-borne particles that entered with the air? The two variables were inseparable in a standard flask. <strong>The constraint move:</strong> Pasteur designed a flask with an S-curved neck. Air enters freely (the broth communicates with the atmosphere) but airborne dust falls into the curve and never reaches the broth. Two identical broths; one in a straight-neck flask, one in a swan-neck. The swan-neck stayed sterile; the straight-neck grew microbes. Result: spontaneous generation disproved; the germ theory of disease becomes mechanistically grounded.
        </p>

        <FindingSubhead>4. Mendel&rsquo;s pea plants (1866) — disentanglement via pure-breeding controls</FindingSubhead>
        <p className="finding-prose">
          The prevailing &ldquo;blending&rdquo; theory of inheritance held that offspring traits average parental traits. The signal was invisible because every real-world cross is genetically noisy. <strong>The constraint move:</strong> Mendel used pure-breeding garden-pea lines (homozygous for the traits he tracked), performed thousands of controlled crosses, and counted trait ratios across F₁ and F₂ generations. The pure-breeding parents constrained the genotype; the large sample constrained the noise; the 3:1 ratio in F₂ became visible because the system was forced to reveal it. Result: discrete factors (later: genes); the foundation of genetics. The work was published in 1866, then ignored for 35 years — not because the constraint failed, but because no one could read the constraint until inheritance had a mechanistic frame to fit into.
        </p>

        <FindingSubhead>5. Michelson-Morley interferometer (1887) — inversion that constrained a null result</FindingSubhead>
        <p className="finding-prose">
          If light traveled through a luminiferous ether, Earth&rsquo;s motion through that ether (~30 km/s) should produce a measurable difference between light traveling parallel vs. perpendicular to Earth&rsquo;s velocity. The two directions were normally confounded because no one could compare them simultaneously. <strong>The constraint move:</strong> Michelson&rsquo;s interferometer split a single light beam into two perpendicular paths and recombined them. Any difference in propagation time would shift the interference fringes. The geometry forced the system to commit — if ether existed and Earth moved through it, fringes <em>must</em> shift. Result: no fringe shift; null result; eventually Einstein&rsquo;s special relativity. The null result was as informative as a positive would have been because the constraint made the experiment binary.
        </p>

        <FindingSubhead>6. Neil deGrasse Tyson&rsquo;s upside-down plant — inversion + disentanglement</FindingSubhead>
        <p className="finding-prose">
          Plants orient by both gravity (gravitropism — roots down, stem up) and light (phototropism — toward photons). In a normal pot, both cues point the same direction; you can&rsquo;t tell which one dominates. <strong>The constraint move:</strong> as a high-school experiment, Tyson grew a plant upside-down (soil held by saran wrap) with light supplied only from below. The two cues now agreed with each other but disagreed with the bottle&rsquo;s vertical. Result: stair-step growth — the plant&rsquo;s stem repeatedly tried gravity-up, then bent toward light, then tried again. The competition between gravitropism and phototropism, normally invisible because they&rsquo;re aligned, became legible as a geometric signature.
        </p>

        <FindingSubhead>7. Tyson&rsquo;s per-human O₂ requirement — Fermi quantification of a design constraint</FindingSubhead>
        <p className="finding-prose">
          &ldquo;Plants give us oxygen&rdquo; is a true claim that&rsquo;s useless for habitat engineering until it has a number on it. <strong>The constraint move:</strong> Fermi-decompose. A human consumes ~550 L of O₂ per day (~770 g). A square meter of dense canopy under optimal light photosynthesizes ~5-10 g of O₂ per day. Therefore minimum plant area per human is on the order of 80-150 m² — a small studio apartment of plants per person, plus the lights to run it plus the power for the lights. The constraint maneuver here is to refuse to accept the qualitative claim and force it into bounded quantities. The bounded number is itself the discovery: closed-habitat Mars settlements become an order-of-magnitude calculation, not a hand-wave.
        </p>
      </FindingSection>

      {/* ── §3 The case-by-type matrix ── */}
      <FindingSection
        eyebrow="§ The matrix"
        title="Which sub-types each case deploys."
      >
        <p className="finding-prose">
          Each case uses at least one of the five sub-types; many deploy two or three in compound. The matrix below makes the pattern legible across the corpus.
        </p>
        <CoOccurrenceMatrix
          title="Case × constraint sub-type — where each case sits in the taxonomy"
          cases={MATRIX_CASES}
          signals={MATRIX_SIGNALS}
        />
        <p className="finding-prose">
          Two observations from the matrix. <strong>First:</strong> sub-types compound. Michelson-Morley used both inversion (the parallel-vs-perpendicular flip) and disentanglement (separating the two normally-confounded directions). Tyson&rsquo;s plant did the same. <strong>Second:</strong> the failed analog (Kelvin) deployed two sub-types — Fermi quantification AND magnification (rate-of-cooling extrapolation) — yet failed. Sub-type coverage is not sufficient; what matters is whether the constraint controls the right variables, which the next section addresses.
        </p>
      </FindingSection>

      {/* ── §4 The exceptions — failed analog ── */}
      <FindingSection
        eyebrow="§ The exceptions"
        title="Kelvin&rsquo;s Earth-age estimate (1862-1897): the failure mode."
      >
        <p className="finding-prose">
          Lord Kelvin computed Earth&rsquo;s age from heat-loss rate. The constraint was rigorous: a molten sphere cooling by conduction loses heat at a calculable rate; given the current temperature gradient at the surface, you can integrate backward to the moment the surface was at melting point. The math was clean. Kelvin arrived at 20-40 million years. Geologists who needed ≥100 million years for stratigraphic deposition were dismissed; Darwinists who needed even more for evolution were embarrassed.
        </p>
        <p className="finding-prose">
          The constraint was correct given Kelvin&rsquo;s variable list. The failure was that <strong>the variable list was incomplete.</strong> Radioactive decay was discovered in 1896 (Becquerel) and identified as a heat source within the Earth by Rutherford and Soddy by 1903. The Earth wasn&rsquo;t cooling under pure conduction; it had a hidden internal heat source that invalidated the rate-of-cooling integration. Modern radiometric dating: Earth is ~4.54 billion years old. Kelvin was off by two orders of magnitude.
        </p>
        <p className="finding-prose">
          <strong>The lesson:</strong> a constraint maneuver is only as good as the variable list it controls. When a hidden source of variation isn&rsquo;t accounted for, the constraint <em>looks</em> rigorous but is actually unconstrained on the dimension that matters. This is the most important failure mode in the pattern. Pasteur succeeded because all the relevant variables (air, particles) were under his control; Kelvin failed because the radioactive-decay variable was outside his theoretical universe entirely.
        </p>
      </FindingSection>

      {/* ── §5 The mechanism ── */}
      <FindingSection
        eyebrow="§ The mechanism"
        title="Why the pattern holds."
      >
        <p className="finding-prose">
          Mechanism: the world contains many variables that are normally confounded — co-varying because they typically appear together. You can&rsquo;t directly observe which is causing what, because both are present in every natural observation. The constraint maneuver works because <strong>you can design a setup where one variable is held constant by physical or logical necessity</strong>, breaking the natural confound. When the held-constant variable is genuinely the right one and the system still produces the effect, you&rsquo;ve isolated the causal contribution of what remains. The harder the constraint is to satisfy without changing the variable of interest, the more conclusive the result.
        </p>
        <p className="finding-prose">
          The five sub-types correspond to five different ways the natural confound can be broken:
        </p>
        <ul className="finding-bullets">
          <li><strong>Disentangle</strong> — break the confound by geometric or biological separation (Pasteur&rsquo;s S-curve, Mendel&rsquo;s pure-breeding).</li>
          <li><strong>Magnify</strong> — break the confound by making one effect detectable at the scale of the apparatus while others remain noise (Cavendish, Galileo).</li>
          <li><strong>Invert</strong> — break the confound by flipping the system relative to one variable, so its independent contribution becomes legible (Michelson-Morley, Tyson&rsquo;s plant).</li>
          <li><strong>Quantify</strong> — break the confound by demanding a number, which forces a decomposition into variables that admit measurement (Fermi estimates).</li>
          <li><strong>Measure</strong> — break the confound by direct observation that displaces inferential reasoning (green-light absorption, chirality and flavor).</li>
        </ul>
        <p className="finding-prose">
          The mechanism upgrades from <em>correlation observed</em> to <em>causal-chain documented</em> because in each case the constraint is sufficient to attribute the effect to the controlled variable specifically, not to anything else that might co-vary with it. Pasteur&rsquo;s swan-neck doesn&rsquo;t just <em>correlate</em> sterility with absence of dust; it <em>excludes</em> all the alternative explanations because the air-flow is identical in both flasks.
        </p>
      </FindingSection>

      {/* ── §6 The boundary ── */}
      <FindingSection
        eyebrow="§ The boundary"
        title="When to stop applying the pattern."
      >
        <p className="finding-prose">
          The constraint maneuver works best when:
        </p>
        <ul className="finding-bullets">
          <li>The variables are enumerable. You can list what&rsquo;s plausibly causing the effect.</li>
          <li>At least one variable can be held constant by physical, geometric, or logical necessity.</li>
          <li>The held-constant variable doesn&rsquo;t require disturbing the system so much that it changes the phenomenon under study.</li>
          <li>The system is reproducible enough that &ldquo;same constraint, same result&rdquo; is meaningful.</li>
        </ul>
        <p className="finding-prose">
          The boundary — when not to apply:
        </p>
        <ul className="finding-bullets">
          <li><strong>The variable list is unknown or unbounded.</strong> Kelvin&rsquo;s failure case. If you don&rsquo;t know all the relevant variables, a clean constraint on the known ones is a false rigor. Climate science circa 1900 had this problem; modern complex-systems work still has it.</li>
          <li><strong>The system is too complex for a single-variable constraint to be informative.</strong> Economic systems with many endogenous variables; ecosystems with many feedbacks. Constraint maneuvers can still work but require multiple controls or natural experiments (Mendel did this; modern econometric identification strategies do this).</li>
          <li><strong>The constraint introduces artifacts that swamp the signal.</strong> Over-disturbing the system. Some quantum measurements have this problem (observer effect not the popular kind, the actual physics one).</li>
          <li><strong>The phenomenon is genuinely emergent and doesn&rsquo;t admit decomposition.</strong> Rare but real. Some questions about consciousness, some questions about social-system tipping points.</li>
        </ul>
      </FindingSection>

      {/* ── §7 How we could be wrong ── */}
      <FindingSection
        eyebrow="§ How we could be wrong"
        title="Honest exposure."
      >
        <p className="finding-prose">
          Four ways this finding could be undershooting or overshooting.
        </p>
        <p className="finding-prose">
          <strong>Cherry-picking.</strong> The seven cases were selected for fit. A random sample of Nobel-class experiments from the last 50 years might or might not produce the same pattern. <em>Falsifier:</em> if &lt;3 of 10 randomly-selected modern Nobel-prize experiments in physics/medicine fit one of the five sub-types cleanly, the pattern is over-narrow or curated. (Pre-registered prediction below.)
        </p>
        <p className="finding-prose">
          <strong>&ldquo;Experimental design 101&rdquo; objection.</strong> Is &ldquo;design a constraint into the experiment&rdquo; just generic experimental practice with a fancier name? Maybe. The defense: the five sub-types make distinct claims (disentanglement is different from magnification; inversion is different from quantification), and the failed-analog mechanism (Kelvin) sharpens the pattern beyond &ldquo;control your variables.&rdquo; The proper test is whether the sub-types are mutually exclusive and collectively exhaustive — which the matrix attempts but doesn&rsquo;t fully validate.
        </p>
        <p className="finding-prose">
          <strong>Retrospective storytelling.</strong> Every successful experiment can be narrated as having &ldquo;used a constraint maneuver&rdquo; after the fact. The proper test of the pattern is <em>prospective</em>: can the lens identify a constraint move that should work BEFORE the experiment is run? (Pre-registered prediction below targets this.)
        </p>
        <p className="finding-prose">
          <strong>Cross-domain over-reach.</strong> The cases are all from natural science. The claim — implicit in classifying this under the &lsquo;strategic&rsquo; domain — is that the constraint maneuver also operates in strategic decisions, business, and operator-class bets. That&rsquo;s plausible but unproven here. <em>Falsifier:</em> if the lens can&rsquo;t identify constraint moves in 3+ documented Strategic Playbook operator cases, the cross-domain claim is overreached. (Pre-registered prediction below.)
        </p>

        <FindingSubhead>Falsifier sensitivity</FindingSubhead>
        <TornadoPlot
          title="Falsifier sensitivity — alternative bar choices on the four wedges of doubt"
          dimensions={FALSIFIER_DIMS}
        />
      </FindingSection>

      {/* ── §8 What this implies ── */}
      <FindingSection
        eyebrow="§ What this implies"
        title="Implications for Lab work and operator-class decisions."
      >
        <p className="finding-prose">
          <strong>For Lab methodology:</strong> the constraint-maneuver lens helps identify which research questions are tractable. If a question doesn&rsquo;t admit a constraint move from at least one of the five sub-types, it&rsquo;s probably not in shape for a Lab finding yet — needs more decomposition. Pre-registration discipline (claim + falsifier band + resolution date + anti-goalpost commitments) is itself a constraint maneuver: it forces the system to commit before resolution data arrives. The recursive observation is that <strong>Lab&rsquo;s entire epistemic discipline is the constraint maneuver applied to the activity of making claims</strong>.
        </p>
        <p className="finding-prose">
          <strong>For practitioners and operators:</strong> when a strategic question feels intractable, ask &ldquo;what&rsquo;s the constraint move?&rdquo; A startup uncertain whether their product-market fit is real can&rsquo;t observe causality from messy signals; a constraint setup (e.g., a forced 1-week sale with no marketing, or an A/B test where the only variable is the pricing page) makes the latent variable legible. An investor uncertain whether a thesis is right vs. confirmation-biased can&rsquo;t observe the difference from a portfolio; a pre-registered prediction with a falsifier date is the constraint move. <FindingCite handle="lab:finding/strategic/2026/asymmetric-underdog-inversion/v1" /> documents three asymmetric-underdog hires that succeeded; the unifying feature was each hire&rsquo;s strategic move was identifiable as a constraint maneuver in their organizational context.
        </p>
        <p className="finding-prose">
          <strong>For the popdec corpus:</strong> the population-decline trilogy (<FindingCite handle="lab:finding/popdec/2026/korea-hungary-divergence/v1" />, <FindingCite handle="lab:finding/popdec/2026/korea-lockin/v1" />, <FindingCite handle="lab:finding/popdec/2026/positive-case-search/v1" />) is itself a constraint maneuver applied to a structural-cultural-vs-policy question that&rsquo;s normally confounded. Korea and Hungary are similar enough on every dimension <em>except</em> spending intensity that the differential isolates spending&rsquo;s causal contribution. The 2030-2035 cohort completion data will resolve the falsifier. The constraint is set; the data&rsquo;s on the clock.
        </p>
      </FindingSection>

      {/* ── §9 Pre-registered predictions ── */}
      <FindingSection
        eyebrow="§ Pre-registered predictions"
        title="What we&rsquo;ll learn over the next 18 months."
      >
        <p className="finding-prose">
          Four predictions test the finding from different angles. Two test prospective usefulness (does the lens identify moves in advance?); two test cross-domain and against-the-grain claims (does it survive random-sample testing? does it transfer to strategy?).
        </p>
        <PredictionsBlock
          title="Pre-registered (2026-05-09)"
          predictions={CONSTRAINT_PREDICTIONS}
        />
      </FindingSection>

      {/* ── §10 What we're watching next ── */}
      <FindingSection
        eyebrow="§ What we&rsquo;re watching next"
        title="Open questions adjacent to this test."
      >
        <ul className="finding-bullets">
          <li><strong>The Kelvin-class failure mode in modern research.</strong> Where are today&rsquo;s &ldquo;rigorous constraints with hidden variables&rdquo;? Candidates: macroeconomic identification strategies that miss endogeneity; ML evaluation benchmarks that miss distribution shift; climate-sensitivity estimates that miss feedback channels. Each is worth its own finding.</li>
          <li><strong>Constraint maneuvers in social science.</strong> Natural experiments (Mariel boatlift, German reunification, Korean War twin separations) are the constraint move in social science. A Form 4 finding on natural-experiment quality could pair with this one.</li>
          <li><strong>The recursive case.</strong> Is Lab&rsquo;s own discipline — pre-registration + falsifier bands + anti-goalpost commitments — provably a constraint maneuver applied to the act of making claims? An explicit finding on this would make the methodology load-bearing.</li>
          <li><strong>Cross-citation network.</strong> This finding cites <FindingCite handle="lab:finding/strategic/2026/asymmetric-underdog-inversion/v1" /> and the popdec trilogy. The reverse direction — those findings re-narrated as constraint maneuvers — would make the methodology layer of the corpus more visible.</li>
        </ul>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 4 — Pattern across cases</dd>
          <dt>Reflexivity</dt><dd>LOW — methodology findings don&rsquo;t affect markets or behaviors the way HIGH-reflexivity findings do; the lens is descriptive</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented · upgrade to causal-chain contingent on prospective-usefulness predictions resolving as forecast</dd>
          <dt>Sample size</dt><dd>N=7 cases + 1 failed analog (Kelvin)</dd>
          <dt>Pre-registration date</dt><dd>2026-05-09</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>A random sample of 10 Nobel-class physics/medicine experiments from the last 50 years would fit one of the five sub-types in fewer than 3 cases.</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). <em>The Constraint Maneuver: How Science Makes the Invisible Legible.</em> Lab Finding <code>{FINDING_META.citationHandle}</code>. Methodology v2.0. Pre-registered 2026-05-09.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-09 — initial pre-registration. Resolution: rolling — predictions resolve 2026-08, 2026-09, 2026-11, 2027-11.</p>
        </div>
      </footer>
    </article>
  );
}
